import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, KeyRound, LogOut } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@mlm/ui';
import { getPublicTenantConfig, logout } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { useTenantBrand } from '../../lib/theme-boot';
import {
  NAV,
  isChildActive,
  isGroupActive,
  visibleNav,
  withTenantFlags,
  type NavGroup,
} from './nav-items';

const EXPAND_KEY = 'mlm.cashier.sidebar.expanded';

/**
 * Cashier shell navigation, in two modes.
 *
 * RAIL (`collapsed`, the till's default): a 98 px icon rail in the tenant's
 * Secondary colour, logo on top, sign-out at the bottom, the active page as a
 * light pill. A group with more than one page opens a flyout, because an icon
 * alone cannot lead to its second and third page. Sized for a finger: every
 * target is at least 48 px and nothing depends on hover.
 *
 * LABELS: the wide sidebar with section names, kept for back-office use.
 *
 * Deliberately a copy of the admin sidebar's behaviour and class strings
 * rather than a shared component: the admin one is welded to admin's auth
 * store, site manifest and route table, and generalising it would mean
 * rewriting production-critical admin surface inside a cashier change. Promote
 * to packages/ui when a third app needs it.
 */
export function CashierSidebar({
  collapsed,
  onToggleCollapsed,
  onNavigate,
  className,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  /** Called after any nav click — used by the mobile sheet to close itself. */
  onNavigate?: () => void;
  className?: string;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // Accounting v1: the Consignment group only exists for tenants that opted
  // in (UI hint — every /pos/consignment* route re-checks the flag).
  const configQuery = useQuery({
    queryKey: ['public-tenant-config'],
    queryFn: getPublicTenantConfig,
    staleTime: 60_000,
    retry: false,
  });
  const consignmentEnabled = configQuery.data?.consignmentEnabled === true;
  const stockistConsignmentEnabled = configQuery.data?.stockistConsignmentEnabled === true;
  const nav = withTenantFlags(
    NAV.filter(
      (g) =>
        (g.label !== 'Consignment' || consignmentEnabled) &&
        (g.label !== 'Stockist Consignment' || stockistConsignmentEnabled),
    ),
    { posTopupEnabled: configQuery.data?.posTopupEnabled === true },
  );
  const items = visibleNav(nav, {
    isSuper: !!user?.roles.includes('super_admin'),
    perms: user?.perms ?? [],
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      return JSON.parse(window.localStorage.getItem(EXPAND_KEY) ?? '{}');
    } catch {
      return {};
    }
  });

  // Auto-expand the active section so the cashier always sees where they are.
  useEffect(() => {
    const active = items.find((i) => i.children && isGroupActive(i, pathname));
    if (active && !expanded[active.label]) {
      setExpanded((prev) => ({ ...prev, [active.label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    window.localStorage.setItem(EXPAND_KEY, JSON.stringify(expanded));
  }, [expanded]);

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase();

  if (collapsed) {
    return (
      <aside
        className={cn(
          'no-print flex w-[98px] shrink-0 flex-col bg-rail text-rail-foreground',
          className,
        )}
      >
        <RailBrand />

        <nav className="flex-1 overflow-y-auto py-2" aria-label="Main">
          <ul className="flex flex-col items-center gap-1">
            {items.map((item) => (
              <li key={item.label} className="w-full">
                <RailItem item={item} pathname={pathname} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </nav>

        {/* Identity + account actions. Changing your own password needs no
            permission, so it lives here rather than in the perm-filtered NAV. */}
        <div className="flex shrink-0 flex-col items-center gap-1 pb-4 pt-2">
          {onToggleCollapsed ? (
            <button
              type="button"
              aria-label="Show menu labels"
              title="Show menu labels"
              className={RAIL_TARGET}
              onClick={onToggleCollapsed}
            >
              <span className={railPill(false)}>
                <ChevronRight className="h-5 w-5" />
              </span>
            </button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="Account" className={RAIL_TARGET}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rail-foreground/15 text-xs font-semibold group-focus-visible:ring-2 group-focus-visible:ring-rail-foreground">
                  {initials}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="min-w-[14rem]">
              <DropdownMenuLabel className="font-normal">
                <div className="truncate text-sm font-medium">{user?.email}</div>
                {user?.roles.length ? (
                  <div className="text-xs text-muted-foreground">{user.roles.join(', ')}</div>
                ) : null}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="min-h-12 text-base"
                onSelect={() => {
                  navigate('/change-password');
                  onNavigate?.();
                }}
              >
                <KeyRound className="h-5 w-5" /> Change password
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            aria-label="Sign out"
            title="Sign out"
            className={RAIL_TARGET}
            onClick={onLogout}
          >
            <span className={railPill(false)}>
              <LogOut className="h-5 w-5" />
            </span>
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className={cn('no-print border-r bg-card flex flex-col w-[240px] shrink-0', className)}>
      <div className="flex items-center justify-between h-14 border-b px-4 shrink-0">
        <span className="font-semibold text-sm tracking-tight">Cashier POS</span>
        {onToggleCollapsed ? (
          <button
            type="button"
            aria-label="Collapse to icon rail"
            title="Collapse to icon rail"
            className="flex h-11 w-11 items-center justify-center -mr-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={onToggleCollapsed}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {items.map((item) => {
            const groupActive = isGroupActive(item, pathname);
            const hasChildren = !!item.children?.length;
            const isOpen = hasChildren && (groupActive || expanded[item.label]);
            return (
              <li key={item.label}>
                <div className="flex items-stretch">
                  <NavLink
                    to={item.to}
                    onClick={onNavigate}
                    className={cn(
                      'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors flex-1',
                      groupActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                  </NavLink>
                  {hasChildren ? (
                    <button
                      type="button"
                      aria-label={`Toggle ${item.label}`}
                      onClick={() => setExpanded((p) => ({ ...p, [item.label]: !p[item.label] }))}
                      className="px-3 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <ChevronDown
                        className={cn('h-4 w-4 transition-transform', !isOpen && '-rotate-90')}
                      />
                    </button>
                  ) : null}
                </div>
                {hasChildren && isOpen ? (
                  <ul className="mt-0.5 ml-7 mb-1 border-l border-border pl-3 space-y-0.5">
                    {item.children!.map((child) => (
                      <li key={child.to}>
                        <NavLink
                          to={child.to}
                          onClick={onNavigate}
                          className={cn(
                            'flex min-h-11 items-center rounded-md px-2 py-1.5 text-sm transition-colors',
                            isChildActive(child, pathname)
                              ? 'text-primary font-medium'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t p-2 space-y-1 shrink-0">
        <div className="px-2 pb-1">
          <div className="text-xs text-muted-foreground truncate" title={user?.email ?? ''}>
            {user?.email}
          </div>
          {user?.roles.length ? (
            <div className="mt-1 inline-block px-2 py-0.5 rounded bg-muted text-[10px] font-medium">
              {user.roles.join(', ')}
            </div>
          ) : null}
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start h-11"
          onClick={() => {
            navigate('/change-password');
            onNavigate?.();
          }}
        >
          <KeyRound className="h-4 w-4 shrink-0" />
          <span className="ml-2">Change password</span>
        </Button>
        <Button variant="outline" className="w-full justify-start h-11" onClick={onLogout}>
          <LogOut className="h-4 w-4 shrink-0" />
          <span className="ml-2">Sign out</span>
        </Button>
      </div>
    </aside>
  );
}

/** A 48 px finger target spanning the rail; the visible pill sits inside it. */
const RAIL_TARGET = 'group flex h-12 w-full items-center justify-center outline-none';

/** The pill around a rail icon: light with a dark icon when it is the page you are on. */
function railPill(active: boolean): string {
  return cn(
    'flex items-center justify-center rounded-[10px] px-[15px] py-[10px] transition-colors',
    'group-focus-visible:ring-2 group-focus-visible:ring-rail-foreground',
    active ? 'bg-rail-foreground text-rail' : 'text-rail-foreground/75',
  );
}

/** Tenant logo on top of the rail; the tenant's initials when it has none. */
function RailBrand() {
  const { name, logoUrl } = useTenantBrand();
  const [broken, setBroken] = useState(false);
  return (
    <div className="flex h-[88px] shrink-0 items-center justify-center px-3">
      {logoUrl && !broken ? (
        <img
          src={logoUrl}
          alt={name}
          className="max-h-14 max-w-[70px] object-contain"
          onError={() => setBroken(true)}
        />
      ) : (
        <span
          className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-rail-foreground/15 text-base font-semibold"
          title={name}
        >
          {(name.length <= 3 ? name : name.slice(0, 2)).toUpperCase()}
        </span>
      )}
    </div>
  );
}

function RailItem({
  item,
  pathname,
  onNavigate,
}: {
  item: NavGroup;
  pathname: string;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const active = isGroupActive(item, pathname);
  const children = item.children ?? [];

  // One page behind the icon: go straight there.
  if (children.length <= 1) {
    return (
      <NavLink
        to={item.to}
        onClick={onNavigate}
        aria-label={item.label}
        title={item.label}
        className={RAIL_TARGET}
      >
        <span className={railPill(active)}>
          <item.icon className="h-5 w-5" />
        </span>
      </NavLink>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={item.label} title={item.label} className={RAIL_TARGET}>
          <span className={railPill(active)}>
            <item.icon className="h-5 w-5" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" className="min-w-[14rem]">
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
          {item.label}
        </DropdownMenuLabel>
        {children.map((child) => (
          <DropdownMenuItem
            key={child.to}
            className={cn(
              'min-h-12 text-base',
              isChildActive(child, pathname) && 'font-semibold text-primary',
            )}
            onSelect={() => {
              navigate(child.to);
              onNavigate?.();
            }}
          >
            {child.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
